import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield, FileText, Scale, AlertTriangle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

const LegalPages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  
  // Extract page name from pathname (e.g., "/terms" -> "terms")
  const page = location.pathname.slice(1);
  
  const pages = {
    'terms': {
      title: 'Terms of Service',
      icon: <FileText className="w-6 h-6" />,
      lastUpdated: 'March 20, 2026',
      content: <TermsContent />
    },
    'privacy': {
      title: 'Privacy Policy',
      icon: <Shield className="w-6 h-6" />,
      lastUpdated: 'March 20, 2026',
      content: <PrivacyContent />
    },
    'refund-policy': {
      title: 'Refund & Billing Policy',
      icon: <Scale className="w-6 h-6" />,
      lastUpdated: 'March 20, 2026',
      content: <RefundContent />
    },
    'acceptable-use': {
      title: 'Acceptable Use Policy',
      icon: <AlertTriangle className="w-6 h-6" />,
      lastUpdated: 'March 20, 2026',
      content: <AcceptableUseContent />
    }
  };
  
  const currentPage = pages[page];
  
  if (!currentPage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Page Not Found</h1>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <span className="text-sm text-muted-foreground">
            Last updated: {currentPage.lastUpdated}
          </span>
        </div>
      </header>
      
      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Title */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
              {currentPage.icon}
            </div>
            <h1 
              className="text-3xl font-bold"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {currentPage.title}
            </h1>
          </div>
          
          {/* Page Content */}
          <div className="prose prose-neutral dark:prose-invert max-w-none">
            {currentPage.content}
          </div>
        </motion.div>
      </main>
      
      {/* Footer Links */}
      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4 max-w-3xl">
          <p className="text-sm text-foreground/70 font-medium mb-4">Other Legal Documents:</p>
          <div className="flex flex-wrap gap-4">
            {Object.entries(pages).map(([key, p]) => (
              key !== page && (
                <Button
                  key={key}
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/${key}`)}
                >
                  {p.title}
                </Button>
              )
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

// ========================
// TERMS OF SERVICE
// ========================
const TermsContent = () => (
  <div className="space-y-6">
    <section>
      <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
      <p className="text-muted-foreground leading-relaxed">
        By accessing or using MockupPro, you agree to be bound by these Terms of Service. 
        If you do not agree to these terms, please do not use our service.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">2. Service Description</h2>
      <p className="text-muted-foreground leading-relaxed">
        MockupPro provides AI-powered tools for generating prompts, mockups, and design assets. 
        The service is provided "as is" without warranties of any kind, either express or implied.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">3. User Content & Ownership</h2>
      <p className="text-muted-foreground leading-relaxed mb-2">
        <strong>You retain full ownership of your uploaded designs.</strong> When you upload content to MockupPro:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>You retain all intellectual property rights to your original designs</li>
        <li>You grant MockupPro a limited license to process your designs for the purpose of generating mockups</li>
        <li>Generated output images are yours to use commercially</li>
        <li>We do not claim ownership of your uploaded or generated content</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">4. Copyright & Content Responsibility</h2>
      <p className="text-muted-foreground leading-relaxed mb-2">
        <strong>You are responsible for ensuring you have the rights to use any content you upload.</strong>
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>Do not upload copyrighted material without proper authorization</li>
        <li>Do not upload trademarked logos or designs without permission</li>
        <li>You represent that you own or have obtained necessary licenses for all uploaded content</li>
        <li>MockupPro is not liable for copyright or trademark infringement by users</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">5. AI-Generated Content Disclaimer</h2>
      <p className="text-muted-foreground leading-relaxed">
        <strong>Generated images are created using AI and may not be 100% accurate representations.</strong> 
        The AI generates creative interpretations of your design on products. Results may vary and 
        should be reviewed before use in commercial listings. MockupPro does not guarantee that 
        generated images will perfectly match physical products.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">6. No Guarantee of Results</h2>
      <p className="text-muted-foreground leading-relaxed">
        We do not guarantee any specific results or outcomes from using our AI tools. 
        Output quality may vary based on inputs, AI model performance, and other factors beyond our control.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">7. User Responsibilities</h2>
      <p className="text-muted-foreground leading-relaxed mb-2">
        You are solely responsible for:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>How you use the generated content</li>
        <li>Ensuring your use complies with applicable laws</li>
        <li>Maintaining the security of your account</li>
        <li>All activities that occur under your account</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">8. Limitation of Liability</h2>
      <p className="text-muted-foreground leading-relaxed">
        <strong>MockupPro is not responsible for misuse of copyrighted or trademarked material.</strong> 
        To the maximum extent permitted by law, MockupPro and its affiliates shall not be liable for 
        any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, 
        revenue, data, or use, arising out of or in connection with your use of the service.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">9. Service Modifications</h2>
      <p className="text-muted-foreground leading-relaxed">
        We reserve the right to modify, suspend, or discontinue any part of the service at any time, 
        with or without notice. We may also modify these terms at any time by posting revised terms on our website.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">10. Account Suspension & Termination</h2>
      <p className="text-muted-foreground leading-relaxed">
        We reserve the right to suspend or terminate accounts that violate these terms, 
        engage in abuse, or exhibit suspicious activity. This includes but is not limited to:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
        <li>Creating multiple accounts to abuse free credits</li>
        <li>Automated or bot-driven usage</li>
        <li>Sharing account credentials</li>
        <li>Generating prohibited content</li>
        <li>Uploading infringing content repeatedly</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">11. Contact</h2>
      <p className="text-muted-foreground leading-relaxed">
        For questions about these terms, please contact us through our feedback page.
      </p>
    </section>
  </div>
);

// ========================
// PRIVACY POLICY
// ========================
const PrivacyContent = () => (
  <div className="space-y-6">
    <section>
      <h2 className="text-xl font-semibold mb-3">1. Information We Collect</h2>
      <p className="text-muted-foreground leading-relaxed mb-2">
        We collect the following types of information:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li><strong>Account Information:</strong> Email address, name, and password (encrypted)</li>
        <li><strong>Usage Data:</strong> Actions performed, credits used, timestamps, and feature interactions</li>
        <li><strong>Payment Information:</strong> Processed securely through Stripe (we do not store card details)</li>
        <li><strong>Generated Content:</strong> Prompts and outputs you create (stored in your gallery)</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">2. How We Use Your Data</h2>
      <p className="text-muted-foreground leading-relaxed mb-2">
        Your data is used to:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>Provide and improve our services</li>
        <li>Process payments and manage subscriptions</li>
        <li>Send important account notifications</li>
        <li>Analyze usage patterns to enhance features</li>
        <li>Prevent fraud and abuse</li>
        <li>Respond to support requests</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">3. Cookies and Tracking</h2>
      <p className="text-muted-foreground leading-relaxed">
        We use essential cookies to maintain your session and preferences (like dark/light mode). 
        We may use analytics to understand how users interact with our platform to improve the experience.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">4. Data Sharing</h2>
      <p className="text-muted-foreground leading-relaxed">
        <strong>We do not sell your personal data.</strong> We may share data with:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
        <li>Payment processors (Stripe) to handle transactions</li>
        <li>AI service providers to process your requests</li>
        <li>Law enforcement when required by law</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">5. Data Protection</h2>
      <p className="text-muted-foreground leading-relaxed">
        We implement industry-standard security measures including:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
        <li>Encrypted passwords (bcrypt hashing)</li>
        <li>HTTPS encryption for all connections</li>
        <li>Secure JWT authentication</li>
        <li>Regular security reviews</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">6. Uploaded Content Storage</h2>
      <p className="text-muted-foreground leading-relaxed mb-2">
        <strong>Regarding uploaded images and designs:</strong>
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>Uploaded images are stored temporarily to process your mockup generation</li>
        <li>We do not use your uploaded designs for any purpose other than generating your requested mockups</li>
        <li>Inactive files may be deleted periodically to free storage</li>
        <li>Generated mockups are stored in your gallery until you delete them</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">7. Data Retention</h2>
      <p className="text-muted-foreground leading-relaxed">
        We retain your account data as long as your account is active. 
        You may request deletion of your account and associated data by contacting us.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">8. Your Rights</h2>
      <p className="text-muted-foreground leading-relaxed">
        You have the right to access, correct, or delete your personal data. 
        Contact us to exercise these rights.
      </p>
    </section>
  </div>
);

// ========================
// REFUND & BILLING POLICY
// ========================
const RefundContent = () => (
  <div className="space-y-6">
    <section>
      <h2 className="text-xl font-semibold mb-3">1. Billing</h2>
      <p className="text-muted-foreground leading-relaxed">
        All subscriptions are billed in advance on a monthly basis. 
        Your subscription will automatically renew at the end of each billing period unless cancelled.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">2. Cancellation</h2>
      <p className="text-muted-foreground leading-relaxed">
        You may cancel your subscription at any time through your account settings. 
        Upon cancellation, you will retain access to your subscription benefits until the end of your current billing period.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">3. Refund Policy</h2>
      <p className="text-muted-foreground leading-relaxed">
        <strong>We do not provide refunds for:</strong>
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
        <li>Unused time within a billing period</li>
        <li>Partial billing periods</li>
        <li>Remaining or unused credits</li>
        <li>Subscriptions that have been used</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">4. Credit System</h2>
      <p className="text-muted-foreground leading-relaxed">
        Credits are provided as part of your subscription and reset each billing period. 
        Unused credits do not roll over to the next period and have no cash value.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">5. Plan Changes</h2>
      <p className="text-muted-foreground leading-relaxed">
        You may upgrade or downgrade your plan at any time. 
        Upgrades take effect immediately with prorated charges. 
        Downgrades take effect at the start of your next billing period.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">6. Disputes</h2>
      <p className="text-muted-foreground leading-relaxed">
        If you believe you have been incorrectly charged, please contact us within 30 days of the charge. 
        We maintain detailed usage logs to help resolve any billing disputes.
      </p>
    </section>
  </div>
);

// ========================
// ACCEPTABLE USE POLICY
// ========================
const AcceptableUseContent = () => (
  <div className="space-y-6">
    <section>
      <h2 className="text-xl font-semibold mb-3">1. Prohibited Content</h2>
      <p className="text-muted-foreground leading-relaxed mb-2">
        You may NOT use MockupPro to generate or process content that is:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>Illegal in any jurisdiction</li>
        <li>Sexually explicit or pornographic</li>
        <li>Violent, threatening, or promoting harm</li>
        <li>Discriminatory or hateful toward any group</li>
        <li>Infringing on copyrights, trademarks, or other intellectual property rights</li>
        <li>Deceptive, fraudulent, or misleading</li>
        <li>Invasive of others' privacy</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">2. Prohibited Activities</h2>
      <p className="text-muted-foreground leading-relaxed mb-2">
        The following activities are strictly prohibited:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1">
        <li>Creating multiple accounts to abuse free credits or trials</li>
        <li>Using automated tools, bots, or scripts to access the service</li>
        <li>Attempting to bypass usage limits or security measures</li>
        <li>Reselling or redistributing the service without authorization</li>
        <li>Reverse engineering or copying our technology</li>
        <li>Interfering with or disrupting the service</li>
        <li>Spamming or flooding the system with requests</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">3. Account Security</h2>
      <p className="text-muted-foreground leading-relaxed">
        You are responsible for maintaining the security of your account. 
        Do not share your login credentials with others. 
        If you suspect unauthorized access, change your password immediately and contact us.
      </p>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">4. Enforcement</h2>
      <p className="text-muted-foreground leading-relaxed">
        We reserve the right to:
      </p>
      <ul className="list-disc pl-6 text-muted-foreground space-y-1 mt-2">
        <li>Investigate suspected violations</li>
        <li>Remove or disable access to violating content</li>
        <li>Suspend or terminate accounts without notice</li>
        <li>Report illegal activities to law enforcement</li>
        <li>Pursue legal action for violations</li>
      </ul>
    </section>
    
    <section>
      <h2 className="text-xl font-semibold mb-3">5. Reporting Violations</h2>
      <p className="text-muted-foreground leading-relaxed">
        If you encounter content or users violating this policy, please report it through our contact page. 
        We take all reports seriously and will investigate promptly.
      </p>
    </section>
  </div>
);

export default LegalPages;
